import React, { useState } from "react";
import { API_BASE } from "../apiConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileImage,
  faDownload,
  faTrash,
  faCalendar,
  faUser,
  faEllipsisV,
} from "@fortawesome/free-solid-svg-icons";
import "./ComplianceDocumentCard.css";
import { formatYyyyDdMm } from "../utils/date";

const ComplianceDocumentCard = ({ document, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const getFileIcon = () => {
    const type = document.fileType?.toLowerCase();

    if (type === "pdf") {
      return (
        <FontAwesomeIcon
          icon={faFilePdf}
          style={{ color: "#f44336", fontSize: "32px" }}
        />
      );
    } else if (type === "xls" || type === "xlsx") {
      return (
        <FontAwesomeIcon
          icon={faFileExcel}
          style={{ color: "#217346", fontSize: "32px" }}
        />
      );
    } else if (type === "jpg" || type === "jpeg" || type === "png") {
      return (
        <FontAwesomeIcon
          icon={faFileImage}
          style={{ color: "#9C27B0", fontSize: "32px" }}
        />
      );
    } else {
      // Word documents or fallback
      return (
        <FontAwesomeIcon
          icon={faFileWord}
          style={{ color: "#2196F3", fontSize: "32px" }}
        />
      );
    }
  };

  const getFileTypeLabel = () => {
    const type = document.fileType?.toLowerCase();

    if (type === "pdf") return "PDF";
    if (type === "docx" || type === "doc") return "Word";
    if (type === "xls" || type === "xlsx") return "Excel";
    if (type === "jpg" || type === "jpeg") return "JPEG";
    if (type === "png") return "PNG";
    return document.fileType?.toUpperCase() || "FILE";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return formatYyyyDdMm(dateString);
  };

  const handleDownload = async () => {
    try {
      const documentId = document.id || document._id;
      const apiBase = API_BASE;

      // Get signed URL from backend
      const response = await fetch(
        `${apiBase}/api/compliance/documents/download/${documentId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get download link");
      }

      const { url } = await response.json();

      // Open signed URL in new tab (works for all file types, no CORS issues)
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Download error:", err);
      alert("Unable to download file. Please try again.");
    }
  };

  const handleDelete = () => {
    setMenuOpen(false);
    const documentId = document.id || document._id;
    onDelete(documentId);
  };

  const isExpiringSoon = document.isExpiringSoon;
  const isExpired = document.isExpired;

  return (
    <div
      className={`compliance-document-card ${isExpired ? "expired" : ""} ${
        isExpiringSoon ? "expiring-soon" : ""
      }`}
    >
      <div className="document-header">
        <div className="document-icon">{getFileIcon()}</div>
        <div className="document-menu">
          <button
            className="menu-toggle-btn"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
          >
            <FontAwesomeIcon icon={faEllipsisV} />
          </button>
          {menuOpen && (
            <div className="document-dropdown-menu">
              <button className="menu-item" onClick={handleDownload}>
                <FontAwesomeIcon icon={faDownload} /> Download
              </button>
              <button className="menu-item delete-item" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="document-content">
        <h4 className="document-name" title={document.fileName}>
          {document.fileName}
        </h4>
        {document.description && (
          <p className="document-description">{document.description}</p>
        )}
      </div>

      <div className="document-metadata">
        <div className="metadata-item">
          <span className="file-type-badge">{getFileTypeLabel()}</span>
          <span className="file-size">{formatFileSize(document.fileSize)}</span>
        </div>

        {document.uploadedBy && (
          <div className="metadata-item">
            <FontAwesomeIcon icon={faUser} className="meta-icon" />
            <span>
              {typeof document.uploadedBy === "object"
                ? document.uploadedBy.name
                : "Unknown"}
            </span>
          </div>
        )}

        <div className="metadata-item">
          <FontAwesomeIcon icon={faCalendar} className="meta-icon" />
          <span>{formatDate(document.createdAt)}</span>
        </div>

        {document.expirationDate && (
          <div className="metadata-item expiration">
            <FontAwesomeIcon icon={faCalendar} className="meta-icon" />
            <span
              className={
                isExpired
                  ? "expired-text"
                  : isExpiringSoon
                  ? "warning-text"
                  : ""
              }
            >
              {isExpired ? "Expired: " : "Expires: "}
              {formatDate(document.expirationDate)}
            </span>
          </div>
        )}
      </div>

      {(isExpired || isExpiringSoon) && (
        <div
          className={`status-banner ${
            isExpired ? "expired-banner" : "warning-banner"
          }`}
        >
          {isExpired ? "⚠️ EXPIRED" : "⏰ EXPIRING SOON"}
        </div>
      )}

      <div className="document-actions">
        <button className="action-btn download-btn" onClick={handleDownload}>
          <FontAwesomeIcon icon={faDownload} /> Download
        </button>
      </div>
    </div>
  );
};

export default ComplianceDocumentCard;
